import { BRAND } from "@/lib/brand";
import { ArrowRight, Headphones } from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-28"
    >
      {/* Background images crossfading */}
      <div className="absolute inset-0">
        <img
          src={BRAND.studioPhoto}
          srcSet="/assets/studio-sm.jpg 800w, /assets/studio.jpg 1600w"
          sizes="100vw"
          alt="CJ Norman in studio"
          fetchpriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-center hero-img hero-img-a"
        />
        <img
          src="/assets/cj2.jpg"
          srcSet="/assets/cj2-sm.jpg 800w, /assets/cj2.jpg 1600w"
          sizes="100vw"
          alt="CJ Norman performing live"
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-center hero-img hero-img-b"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/80 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        {/* glow accents */}
        <div className="absolute -top-20 -left-20 w-[420px] h-[420px] rounded-full bg-brand-violet/30 blur-[140px] animate-glow-pulse" />
        <div className="absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-brand-orange/20 blur-[160px] animate-glow-pulse" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-8 animate-float-up">
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-white/15 bg-white/5 backdrop-blur-sm mb-8 max-w-full">
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-magenta opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-magenta"></span>
            </span>
            <span className="font-body text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-white/80 whitespace-nowrap">
              {BRAND.hero_subtagline}
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display font-black text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tighter leading-[0.95] text-white"
          >
            Where{" "}
            <span className="text-eq-gradient">Vision</span>
            <br />
            Meets{" "}
            <span className="relative inline-block">
              Sound
              <svg
                className="absolute -bottom-2 left-0 w-full"
                viewBox="0 0 200 12"
                fill="none"
              >
                <path
                  d="M2 9C30 4 80 4 198 9"
                  stroke="url(#hg)"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient id="hg" x1="0" y1="0" x2="200" y2="0">
                    <stop offset="0" stopColor="#D946EF" />
                    <stop offset="1" stopColor="#F97316" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
            .
          </h1>

          <p className="mt-8 max-w-2xl font-body text-base sm:text-lg text-white/70 leading-relaxed">
            {BRAND.producer} — Producer, Arranger and Engineer behind live
            recordings, studio sessions and chart-ready masters for gospel,
            R&B and worship artists across the country.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#quote"
              data-testid="hero-cta-quote"
              className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-white text-black font-display font-bold tracking-tight hover:bg-brand-magenta hover:text-white transition-all duration-300"
            >
              Request a Quote
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>
            <a
              href="#portfolio"
              data-testid="hero-cta-portfolio"
              className="group inline-flex items-center gap-3 px-7 py-4 rounded-full border border-white/20 bg-white/5 backdrop-blur-md text-white font-display font-bold tracking-tight hover:bg-white/10 transition-all duration-300"
            >
              <Headphones size={18} />
              Hear the Work
            </a>
          </div>
        </div>

        {/* Right column: animated EQ + stats */}
        <div className="lg:col-span-4 hidden lg:flex flex-col gap-6 animate-float-up">
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-8">
            <div className="flex items-end gap-2 h-32">
              {[
                "bg-brand-violet",
                "bg-brand-magenta",
                "bg-fuchsia-400",
                "bg-brand-cyan",
                "bg-emerald-400",
                "bg-yellow-400",
                "bg-brand-orange",
                "bg-orange-600",
                "bg-brand-magenta",
                "bg-brand-cyan",
              ].map((c, i) => (
                <div
                  key={i}
                  className={`eq-bar flex-1 rounded-t-md ${c}`}
                  style={{
                    height: `${30 + ((i * 37) % 70)}%`,
                    animationDelay: `${i * 0.12}s`,
                    animationDuration: `${1 + (i % 3) * 0.3}s`,
                  }}
                />
              ))}
            </div>
            <div className="mt-6 flex items-center justify-between">
              <div>
                <p className="font-display font-black text-3xl text-white">
                  20+
                </p>
                <p className="font-body text-xs uppercase tracking-[0.2em] text-white/50 mt-1">
                  Artists
                </p>
              </div>
              <div>
                <p className="font-display font-black text-3xl text-eq-gradient">
                  6
                </p>
                <p className="font-body text-xs uppercase tracking-[0.2em] text-white/50 mt-1">
                  Services
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-brand-magenta/15 via-transparent to-brand-orange/15 p-6">
            <p className="font-body text-xs uppercase tracking-[0.25em] text-white/60">
              Now Booking
            </p>
            <p className="font-display font-bold text-xl text-white mt-2">
              Live and Studio Sessions
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
